import { useEffect, useState } from 'react';
import { supabase } from '@/lib/supabase';
import { mensagemAmigavel } from '@/lib/mensagemAmigavel';
import { basePathSite } from '@/pages/assistente-evento/utils';

export interface EventoPublicar {
  id: number;
  nome: string;
  slug: string;
  inicio_em: string | null;
  local_nome: string | null;
  cidade: string | null;
  capa: string | null;
  vende_produtos: boolean;
}

interface Props {
  evento: EventoPublicar;
  temParticipacao: boolean;
  aoFechar: () => void;
  aoPublicado: () => void;
}

interface ItemCheck {
  rotulo: string;
  pronta: boolean;
}

// Modal de publicar: mostra o checklist do que falta e confirma a publicação.
export default function ModalPublicar({
  evento,
  temParticipacao,
  aoFechar,
  aoPublicado,
}: Props) {
  const [temProduto, setTemProduto] = useState(false);
  const [publicando, setPublicando] = useState(false);
  const [publicado, setPublicado] = useState(false);
  const [copiado, setCopiado] = useState(false);
  const [erro, setErro] = useState<string | null>(null);

  useEffect(() => {
    if (!evento.vende_produtos) return;
    let ativo = true;
    supabase
      .rpc('loja_produtos_do_evento', { p_evento: evento.id })
      .then(({ data }) => {
        if (ativo) setTemProduto(((data as unknown[]) || []).length > 0);
      })
      .catch(() => {
        /* não conta produto */
      });
    return () => {
      ativo = false;
    };
  }, [evento.id, evento.vende_produtos]);

  const itens: ItemCheck[] = [
    { rotulo: 'Capa do evento', pronta: !!evento.capa },
    { rotulo: 'Data de início', pronta: !!evento.inicio_em },
    { rotulo: 'Local', pronta: !!(evento.local_nome || evento.cidade) },
    { rotulo: 'Participação ou fichas', pronta: temParticipacao || temProduto },
  ];

  const faltam = itens.filter((i) => !i.pronta).length;
  const urlEvento = `${window.location.origin}${basePathSite()}/e/${evento.slug}`;

  async function publicar() {
    setPublicando(true);
    setErro(null);
    try {
      const { error } = await supabase
        .from('eventos')
        .update({ status: 'publicado', publicado_em: new Date().toISOString() })
        .eq('id', evento.id);
      if (error) throw error;
      setPublicado(true);
      aoPublicado();
    } catch (e) {
      setErro(mensagemAmigavel(e));
    } finally {
      setPublicando(false);
    }
  }

  async function copiar() {
    try {
      await navigator.clipboard.writeText(urlEvento);
    } catch {
      /* silencioso */
    }
    setCopiado(true);
    setTimeout(() => setCopiado(false), 2000);
  }

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center overflow-y-auto bg-black/40 p-4">
      <div className="w-full max-w-[480px] rounded-lg border border-secondary-200 bg-white">
        <div className="flex items-center justify-between border-b border-secondary-200 px-5 py-4">
          <h3 className="text-base font-medium text-foreground-950">Publicar evento</h3>
          {!publicado && (
            <button
              type="button"
              onClick={aoFechar}
              className="flex h-8 w-8 cursor-pointer items-center justify-center rounded-md text-secondary-500 transition-colors hover:bg-background-100"
              aria-label="Fechar"
            >
              <i className="ri-close-line text-xl" />
            </button>
          )}
        </div>

        <div className="px-5 py-5">
          {publicado ? (
            <div className="text-center">
              <span className="mx-auto flex h-14 w-14 items-center justify-center rounded-full bg-accent-100 text-accent-700">
                <i className="ri-rocket-2-line text-2xl" />
              </span>
              <h4 className="mt-3 text-lg font-semibold text-foreground-950">
                Publicado. Seu evento está no ar.
              </h4>
              <button
                type="button"
                onClick={copiar}
                className="mt-4 w-full cursor-pointer whitespace-nowrap rounded-md bg-primary-500 px-6 py-3 text-sm font-semibold text-white transition-colors hover:bg-primary-600"
              >
                {copiado ? 'Link copiado!' : 'Copiar link'}
              </button>
            </div>
          ) : (
            <>
              <ul className="space-y-2.5">
                {itens.map((i) => (
                  <li key={i.rotulo} className="flex items-center gap-2.5">
                    <span
                      className={`flex h-5 w-5 items-center justify-center ${
                        i.pronta ? 'text-success-600' : 'text-warning-500'
                      }`}
                    >
                      <i className={i.pronta ? 'ri-checkbox-circle-line' : 'ri-error-warning-line'} />
                    </span>
                    <span
                      className={`text-sm ${i.pronta ? 'text-foreground-800' : 'text-foreground-900'}`}
                    >
                      {i.rotulo}
                    </span>
                  </li>
                ))}
              </ul>

              {faltam > 0 && (
                <p className="mt-3 rounded-md bg-warning-50 px-3 py-2 text-sm text-warning-800">
                  Falta {faltam} {faltam === 1 ? 'item' : 'itens'} para colocar no ar.
                </p>
              )}

              {erro && (
                <p className="mt-2 rounded-md bg-danger-50 px-3 py-2 text-sm text-danger-700">
                  {erro}
                </p>
              )}

              <button
                type="button"
                onClick={publicar}
                disabled={publicando || faltam > 0}
                className="mt-4 flex w-full cursor-pointer items-center justify-center gap-2 whitespace-nowrap rounded-md bg-primary-500 px-6 py-3 text-sm font-semibold text-white transition-colors hover:bg-primary-600 disabled:cursor-not-allowed disabled:bg-secondary-200 disabled:text-secondary-500"
              >
                {publicando && (
                  <span className="flex h-4 w-4 items-center justify-center">
                    <i className="ri-loader-4-line animate-spin" />
                  </span>
                )}
                {publicando ? 'Publicando…' : 'Publicar evento'}
              </button>
            </>
          )}
        </div>
      </div>
    </div>
  );
}